import React from 'react'
import { useDispatch } from 'APP/Hooks/redux'
import { Image, View, Text, TouchableOpacity, type TextProps } from 'react-native'
import DialogueMarkdown from 'APP/Components/DialogueMarkdown'
import { Images, Colors, Metrics } from 'APP/Themes'
import MaterialIcon from 'react-native-vector-icons/MaterialCommunityIcons'
import I18n from 'APP/I18n'
import PatientHistoryActions from 'APP/Redux/PatientHistoryRedux'
import Analytics from 'APP/Services/Analytics'
import sprigClient from 'APP/Services/Sprig'
import * as NestedNavHelper from 'APP/Nav/NestedNavHelper'
import { RootScreens } from 'APP/Lib/Enums'

import Styles, { markdownStyles } from './style'
import { logger } from 'APP/Lib/Datadog'
import { FlowType } from 'APP/Services/Silkroad/types'
import {
  RecommendationActionType,
  type Recommendation,
  type RecommendationAction,
} from 'APP/Store/WellBeingAssessment/types'

const navigationLinks: Record<string, RootScreens> = {
  icbt_toolkits: RootScreens.mind,
}

type DefaultRecommendationProps = Recommendation & {
  onActionPress?: (action: RecommendationAction) => void
  /* TODO: verify if this is actually passed to the component, it doesn't seem to be */
  score?: unknown
  style: TextProps['style']
}

function DefaultRecommendation({
  recommendation: recommendationText,
  actions,
  onActionPress: onActionPressProp,
  score,
  style,
}: DefaultRecommendationProps) {
  const dispatch = useDispatch()
  const navigateToLink = (link: string) => {
    try {
      const routeName = navigationLinks[link]
      /* istanbul ignore next */
      if (NestedNavHelper.getRouteNames().indexOf(routeName) === -1) {
        logger.error('Invalid route name from recommendation link', { link, routeName })
        return
      }

      NestedNavHelper.navigate(routeName)
    } catch (err) {
      logger.error('Failed to navigate to recommendation link', {}, err)
    }
  }

  const onActionPress = (action: RecommendationAction) => {
    const actionType = action.action
    sprigClient.track('WBI_RECOMMENDATION_CLICKED_' + String(actionType).toUpperCase())
    Analytics.trackEvent('button_click', {
      button_value: actionType,
      trigger: 'wbi recommendation',
      screen_name: 'WellBeingIndexScreen.wellBeingIndexRecommendation',
      wbi_score: score,
    })

    if (typeof onActionPressProp === 'function') {
      onActionPressProp(action)
      return
    }

    switch (actionType) {
      case RecommendationActionType.newConsult:
        // We lose the benefits of the discriminated union that represents recommendations,
        // because BaseAction is part of the union in the backend (due to it being on BaseRecommendation
        // and WBICompare, WBIProgress not overridding with a more concrete type).
        // So we have to check again for the presence of the key to satisfy the typechecker
        // FIXME: restructure the contract for recommendations
        // We don't respect the value of command_post, it makes things confusing
        if ('command_post' in action && action.command_post) {
          dispatch(
            PatientHistoryActions.startEpisodeWithFlow(FlowType.mental_health, 'app-mental-health')
          )
        }
        return
      case RecommendationActionType.link:
        if ('link' in action) {
          navigateToLink(action.link)
        }
    }
  }

  return (
    <View style={[Styles.container, style]}>
      <View style={Styles.author}>
        <Image source={Images.avatarDialogue} style={Styles.authorIcon} />
      </View>
      <View style={Styles.textBubbleContainer}>
        {/* @ts-expect-error Ttype inference does not work well with JS styles */}
        <Text style={Styles.authorLabel}>
          {I18n.t('WellBeingIndexScreen.recommendationAuthor', { clinic: I18n.t('Common.clinic') })}
        </Text>
        <View style={Styles.textBubble} testID="textBubble">
          {/* @ts-expect-error TODO: convert DialogueMarkdown to Typescript */}
          <DialogueMarkdown
            markdown={recommendationText?.[I18n.baseLocale]}
            styles={markdownStyles}
          />
          <View style={Styles.textBubbleTail} />
        </View>
        {actions?.map((action, i) => (
          <TouchableOpacity
            key={i}
            onPress={() => onActionPress(action)}
            style={Styles.action}
            testID="recommendationAction"
          >
            {/* @ts-expect-error Ttype inference does not work well with JS styles */}
            <Text style={Styles.actionText} numberOfLines={2}>
              {action.text[I18n.baseLocale]}
            </Text>
            <MaterialIcon
              accessibilityRole="button"
              color={Colors.accent}
              size={Metrics.icons.small}
              name="chevron-right"
            />
          </TouchableOpacity>
        ))}
      </View>
    </View>
  )
}

export default DefaultRecommendation
